import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MaintenanceWorkloadService } from '../services/maintenance-workload.service';
import type { MaintenanceWorkload, WorkloadFilters } from '../types/maintenance-workload.types';
import { useState } from 'react';

export function useMaintenanceWorkloads(companyId: string, initialFilters: WorkloadFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<WorkloadFilters>({
    page: 1,
    pageSize: 10,
    status: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['maintenance-workloads', companyId, filters],
    queryFn: () => MaintenanceWorkloadService.getWorkloads(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['maintenance-workload-stats', companyId],
    queryFn: () => MaintenanceWorkloadService.getWorkloadStats(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<MaintenanceWorkload>) => MaintenanceWorkloadService.createWorkload(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-workloads'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<MaintenanceWorkload> }) => 
      MaintenanceWorkloadService.updateWorkload(id, updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-workloads'] });
      queryClient.invalidateQueries({ queryKey: ['maintenance-workloads', 'detail', variables.id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => MaintenanceWorkloadService.deleteWorkload(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-workloads'] });
    },
  });

  const calculateMutation = useMutation({
    mutationFn: (id: string) => MaintenanceWorkloadService.calculateWorkload(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['maintenance-workloads', 'detail', id] });
    },
  });

  return {
    ...query,
    workloads: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    filters,
    setFilters,
    createWorkload: createMutation.mutateAsync,
    updateWorkload: updateMutation.mutateAsync,
    deleteWorkload: deleteMutation.mutateAsync,
    calculateWorkload: calculateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isCalculating: calculateMutation.isPending,
  };
}

export function useMaintenanceWorkloadDetail(id?: string) {
  return useQuery({
    queryKey: ['maintenance-workloads', 'detail', id],
    queryFn: () => MaintenanceWorkloadService.getWorkloadDetail(id!),
    enabled: !!id,
  });
}

export function useWorkloadRules(companyId: string) {
  return useQuery({
    queryKey: ['maintenance-workload-rules', companyId],
    queryFn: () => MaintenanceWorkloadService.getWorkloadRules(companyId),
    enabled: !!companyId,
  });
}
